<template>
    <div class="faq-container" role="main">
        <h1 class="faq-title" id="faq-title">FAQ</h1>
        
        <div class="faq-themes">
            <div v-for="theme in themes" 
                 :key="theme.id" 
                 class="faq-theme"
                 role="region"
                 :aria-labelledby="'theme-title-' + theme.id">
                <button class="theme-header" 
                        @click="toggleTheme(theme)"
                        :aria-expanded="openThemes[theme.id] ? 'true' : 'false'"
                        :aria-controls="'theme-content-' + theme.id">
                    <h2 class="left-bullet" :id="'theme-title-' + theme.id">{{ $i18n.locale === 'en' ? theme.title_en : theme.title }}</h2>
                    <span class="toggle-icon" :class="{ 'is-open': openThemes[theme.id] }" aria-hidden="true">
                        <i class="fas fa-chevron-down"></i>
                    </span>
                </button>
                
                <div v-if="openThemes[theme.id]" 
                     class="theme-items"
                     :id="'theme-content-' + theme.id"
                     role="region"
                     :aria-labelledby="'theme-title-' + theme.id">
                    <div v-for="item in theme.items" 
                         :key="item.id" 
                         class="faq-item"
                         role="group"
                         :aria-labelledby="'question-' + theme.id + '-' + item.id"
                         :class="{ 'is-open': openItems[getItemKey(theme.id, item.id)] }">
                        <button class="item-question" 
                                @click="toggleItem(theme, item)"
                                :id="'question-' + theme.id + '-' + item.id"
                                :aria-expanded="openItems[getItemKey(theme.id, item.id)] ? 'true' : 'false'"
                                :aria-controls="'answer-' + theme.id + '-' + item.id">
                            {{ $i18n.locale === 'en' ? item.question_en : item.question }}
                            <span class="toggle-icon" aria-hidden="true">
                                <i class="fas" :class="openItems[getItemKey(theme.id, item.id)] ? 'fa-minus' : 'fa-plus'"></i>
                            </span>
                        </button>
                        <div v-if="openItems[getItemKey(theme.id, item.id)]" 
                             class="item-answer" 
                             :id="'answer-' + theme.id + '-' + item.id"
                             role="region"
                             v-html="$i18n.locale === 'en' ? item.answer_en : item.answer"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'FAQ',
    
    data() {
        return {
            themes: null,
            openThemes: {},
            openItems: {}
        }
    },

    beforeRouteEnter(to, from, next) {
        Vue.axios.get('/faq').then(({data}) => {
            next(vm => vm.setData(data));
            console.log('FAQ data loaded:', data);
        }).catch(error => {
            console.error('Erreur lors du chargement de la FAQ:', error);
            next(vm => vm.setData([]));
        });
    },

    mounted() {
        // Ajouter les gestionnaires d'événements pour la navigation au clavier
        window.addEventListener('keydown', this.handleKeyDown);
    },

    beforeDestroy() {
        // Nettoyer les gestionnaires d'événements
        window.removeEventListener('keydown', this.handleKeyDown);
    },

    methods: {
        setData(data) {
            this.themes = data;
        },

        getItemKey(themeId, itemId) {
            return `${themeId}-${itemId}`;
        },

        toggleTheme(theme) {
            this.$set(this.openThemes, theme.id, !this.openThemes[theme.id]);
            
            // Si on ferme le thème, on ferme aussi toutes ses questions
            if (!this.openThemes[theme.id]) {
                theme.items.forEach(item => {
                    this.$set(this.openItems, this.getItemKey(theme.id, item.id), false);
                });
            }
        },
        
        toggleItem(theme, item) {
            const key = this.getItemKey(theme.id, item.id);
            this.$set(this.openItems, key, !this.openItems[key]);
        },

        handleKeyDown(event) {
            const target = event.target;
            
            // Si nous sommes sur un bouton de thème ou de question
            if (target.matches('.theme-header, .item-question')) {
                switch (event.key) {
                    case 'Enter':
                    case ' ':
                        event.preventDefault();
                        target.click();
                        break;
                    case 'ArrowDown':
                        event.preventDefault();
                        this.focusNextElement(target);
                        break;
                    case 'ArrowUp':
                        event.preventDefault();
                        this.focusPreviousElement(target);
                        break;
                    case 'Home':
                        event.preventDefault();
                        this.focusFirstElement();
                        break;
                    case 'End':
                        event.preventDefault();
                        this.focusLastElement();
                        break;
                }
            }
        },

        focusNextElement(currentElement) {
            const focusableElements = this.getFocusableElements();
            const currentIndex = focusableElements.indexOf(currentElement);
            const nextElement = focusableElements[currentIndex + 1];
            if (nextElement) nextElement.focus();
        },

        focusPreviousElement(currentElement) {
            const focusableElements = this.getFocusableElements();
            const currentIndex = focusableElements.indexOf(currentElement);
            const previousElement = focusableElements[currentIndex - 1];
            if (previousElement) previousElement.focus();
        },

        focusFirstElement() {
            const focusableElements = this.getFocusableElements();
            if (focusableElements.length > 0) focusableElements[0].focus();
        },

        focusLastElement() {
            const focusableElements = this.getFocusableElements();
            if (focusableElements.length > 0) {
                focusableElements[focusableElements.length - 1].focus();
            }
        },

        getFocusableElements() {
            return Array.from(
                this.$el.querySelectorAll('.theme-header, .item-question:not([disabled])')
            );
        }
    }
}
</script>
